import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Wrench, Images, HardDriveDownload, Loader2, CheckCircle2, RefreshCw, Square } from "lucide-react";
import { toast } from "sonner";

function StatRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5">
      <span className="text-slate-400">{label}</span>
      <span className="font-mono text-amber-300" data-testid={testId}>{value}</span>
    </div>
  );
}

export default function Manutenzione() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [relabelBusy, setRelabelBusy] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/maintenance/backfill-thumbs/status");
      setStatus(data);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, []);

  const running = !!status?.running;

  const stopBackfill = async () => {
    setStopBusy(true);
    try {
      await api.post("/admin/maintenance/backfill-thumbs/stop");
      if (pollRef.current) clearTimeout(pollRef.current);
      setBackfillBusy(false);
      toast.success("Backfill interrotto");
      await loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStopBusy(false); }
  };

  const startBackfill = async () => {
    setBackfillBusy(true);
    try {
      await api.post("/admin/maintenance/backfill-thumbs");
      toast.success("Backfill miniature avviato");
      const poll = async () => {
        const d = await loadStatus();
        if (d?.running) { pollRef.current = setTimeout(poll, 3000); }
        else { setBackfillBusy(false); if (d?.thumbs?.done) toast.success(`Backfill completato (${d.thumbs.count ?? 0} miniature)`); }
      };
      pollRef.current = setTimeout(poll, 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBackfillBusy(false); }
  };

  const runRelabel = async () => {
    setRelabelBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/relabel-gcs-to-wasabi");
      toast.success(`Record allineati a Wasabi: ${data.updated ?? 0}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRelabelBusy(false); }
  };

  const t = status?.thumbs || {};
  const p = status?.pdf_pages || {};
  const pct = t.total ? Math.min(100, ((t.processed ?? t.count ?? 0) / t.total) * 100).toFixed(0) : 0;

  return (
    <div data-testid="manutenzione-page">
      <PageHeader
        title="Manutenzione"
        subtitle="Operazioni di manutenzione su miniature allegati e allineamento dello storage. Le attività girano in background sul server."
        icon={Wrench}
        testId="manutenzione-header"
        actions={
          <Button onClick={loadStatus} disabled={loading} variant="outline" data-testid="manutenzione-refresh-btn"
            className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna
          </Button>
        }
      />

      <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
        <div className="pgp-card p-6" data-testid="card-backfill">
          <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2 mb-1">
            <Images className="h-4 w-4 text-amber-400" /> Backfill miniature
          </h3>
          <p className="text-sm text-slate-400 mb-4">Genera le miniature mancanti (foto + PDF) e il conteggio pagine per tutti gli studi. Ripristinabile dopo un'interruzione.</p>

          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
            <StatRow label="Miniature" value={`${t.processed ?? t.count ?? 0}/${t.total ?? 0}`} testId="stat-thumbs" />
            <div className="mt-1 mb-2 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <StatRow label="Stato" value={running ? "in corso…" : (t.done ? "completato" : (t.total ? "inattivo (interrotto)" : "non avviato"))} testId="stat-thumbs-state" />
            {(t.failed ?? 0) > 0 && <StatRow label="Miniature saltate (errore)" value={t.failed} testId="stat-thumbs-failed" />}
            <StatRow label="Pagine PDF calcolate" value={`${p.count ?? 0}/${p.total ?? 0}`} testId="stat-pdf-pages" />
          </div>

          <Button onClick={startBackfill} disabled={backfillBusy || running} data-testid="btn-start-backfill"
            className="mt-4 w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {(backfillBusy || running) ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Backfill in corso…</> : <>{t.total && !t.done ? "Riprendi backfill miniature" : "Avvia backfill miniature"}</>}
          </Button>
          {(running || backfillBusy) && (
            <Button onClick={stopBackfill} disabled={stopBusy} variant="outline" data-testid="btn-stop-backfill"
              className="mt-2 w-full border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-600 font-semibold">
              {stopBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Square className="h-4 w-4 mr-2" />} Interrompi
            </Button>
          )}
        </div>

        <div className="pgp-card p-6" data-testid="card-relabel">
          <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2 mb-1">
            <HardDriveDownload className="h-4 w-4 text-cyan-400" /> Allinea storage GCS → Wasabi
          </h3>
          <p className="text-sm text-slate-400 mb-4">Aggiorna i record allegato ancora etichettati come Google Cloud impostandoli su Wasabi, per gli studi il cui provider attuale è Wasabi. Allinea anche i riferimenti alle miniature.</p>

          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 flex items-start gap-2 text-xs text-slate-400">
            <CheckCircle2 className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
            <span>Operazione idempotente e sicura: interessa solo gli studi con provider Wasabi; gli studi ancora su GCS non vengono toccati.</span>
          </div>

          <Button onClick={runRelabel} disabled={relabelBusy} data-testid="btn-run-relabel"
            className="mt-4 w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
            {relabelBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Allineamento…</> : <>Allinea GCS → Wasabi</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
